import { useEffect, useState } from "react";
import {
  Search,
  RotateCcw,
  LoaderCircle,
  Calendar,
  User,
  FileText,
  Package,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../constant/api";

export default function GarmentsReturnsList() {
  const [returnsList, setReturnsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchAllReturns();
  }, []);

  const fetchAllReturns = async () => {
    setLoading(true);
    try {
      const res = await api.get("/GarmentsProduct/getAllReturns");
      if (res.data?.success) {
        setReturnsList(res.data.data || []);
      }
    } catch (error) {
      toast.error("Error loading return history");
    } finally {
      setLoading(false);
    }
  };

  // Filter logic (Search query + Date filters)
  const filteredReturns = returnsList.filter((item) => {
    const q = searchQuery.toLowerCase();
    const matchesQuery =
      item.product_name?.toLowerCase().includes(q) ||
      item.invoice_no?.toLowerCase().includes(q) ||
      item.customer_name?.toLowerCase().includes(q) ||
      item.phone?.toLowerCase().includes(q);

    // Date filtering
    let matchesDate = true;
    if (startDate && endDate) {
      const itemDate = new Date(item.return_date).toISOString().split("T")[0];
      matchesDate = itemDate >= startDate && itemDate <= endDate;
    }

    return matchesQuery && matchesDate;
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-600 text-white shadow-md">
              <RotateCcw size={20} />
            </div>
            <div>
              <h1 className="text-base font-extrabold text-slate-900 sm:text-xl">
                Product Returns History
              </h1>
              <p className="text-[11px] text-slate-500 sm:text-xs">Track all returned garments & stock logs</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-3 pt-4 sm:px-6 sm:pt-6 space-y-4">
        {/* Filter Controls Box */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-4 rounded-3xl border border-slate-200 shadow-sm">
          {/* Search Input */}
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search product, invoice, customer..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 pl-10 pr-4 py-2.5 text-xs sm:text-sm text-slate-800 outline-none transition focus:border-amber-500 focus:bg-white"
            />
          </div>

          {/* Start Date Filter */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-slate-500">From:</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs font-semibold text-slate-800 outline-none focus:border-amber-500 focus:bg-white"
            />
          </div>

          {/* End Date Filter */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-slate-500">To:</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs font-semibold text-slate-800 outline-none focus:border-amber-500 focus:bg-white"
            />
          </div>
        </div>

        {/* Returns Table / Content */}
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <LoaderCircle size={28} className="animate-spin text-amber-600" />
            <p className="text-xs font-medium text-slate-500">Loading returns history...</p>
          </div>
        ) : filteredReturns.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white p-6 text-center">
            <RotateCcw size={36} className="text-slate-300 mb-2" />
            <h3 className="text-sm font-bold text-slate-800">No return records found</h3>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="p-3.5 font-semibold">Invoice #</th>
                  <th className="p-3.5 font-semibold">Customer Details</th>
                  <th className="p-3.5 font-semibold">Product Name</th>
                  <th className="p-3.5 font-semibold">Variant</th>
                  <th className="p-3.5 font-semibold text-center">Returned Qty</th>
                  <th className="p-3.5 font-semibold text-right">Return Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReturns.map((ret) => (
                  <tr key={ret.return_id} className="hover:bg-slate-50/60 transition">
                    <td className="p-3.5 font-mono font-bold text-amber-700">
                      #{ret.invoice_no || "N/A"}
                    </td>
                    <td className="p-3.5">
                      <div className="font-bold text-slate-900">{ret.customer_name || "Walk-in"}</div>
                      <div className="text-[11px] text-slate-500">{ret.phone}</div>
                    </td>
                    <td className="p-3.5 font-bold text-slate-800">{ret.product_name}</td>
                    <td className="p-3.5 text-slate-500 font-medium">{ret.size} / {ret.color}</td>
                    <td className="p-3.5 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg bg-amber-50 text-amber-700 font-extrabold">
                        -{ret.return_qty} pcs
                      </span>
                    </td>
                    <td className="p-3.5 text-right font-medium text-slate-500">
                      {new Date(ret.return_date).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}